<template>
    <div class="staff-v3-container">
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <div class="d-flex flex-row align-items-center" :style="{ marginTop: '10px', marginBottom: normalizeSize(20, true) }">
                <h1
                    class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0 title-header"
                    :style="{ fontSize: normalizeFontSize(26) }"
                >
                    {{ isEdit ? $t('staff.edit_employee') : $t('staff.add_employee') }}
                </h1>
            </div>

            <div class="ppv2-w-full">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Left Column -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- Main Information -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6">
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('staff.table.columns.last_name')"
                                        v-model="form.last_name"
                                        :placeholder="$t('staff.form.last_name_placeholder')"
                                        :required="true"
                                        :errorText="errors.last_name"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('staff.table.columns.first_name')"
                                        v-model="form.first_name"
                                        :placeholder="$t('staff.form.first_name_placeholder')"
                                        :required="true"
                                        :errorText="errors.first_name"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('staff.table.columns.email')"
                                        v-model="form.email"
                                        type="email"
                                        :placeholder="$t('staff.form.email_placeholder')"
                                        :required="true"
                                        :errorText="errors.email"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('profil.phone_number.label')"
                                        v-model="form.phone_number"
                                        :placeholder="$t('staff.form.phone_placeholder')"
                                        :required="true"
                                        :errorText="errors.phone_number"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        @keydown.space.native.prevent
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('profil.password_modal.new')"
                                        v-model="form.password"
                                        type="password"
                                        :placeholder="
                                            isEdit
                                                ? $t('staff.form.password_edit_placeholder')
                                                : $t('staff.form.password_placeholder')
                                        "
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('profil.pin.label')"
                                        v-model="form.pin"
                                        type="password"
                                        :errorText="errors.pin"
                                        :required="!isEdit"
                                        :placeholder="$t('profil.pin.placeholder')"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        maxlength="4"
                                        digits-only
                                    />
                                </div>
                            </div>
                        </v-card>

                        <!-- Affectations -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('staff.assignments') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6">
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mb-6">
                                    <BaseSelect
                                        :label="$t('staff.roles')"
                                        v-model="form.role"
                                        :options="roleOptions"
                                        :required="required"
                                        :error="errors.role"
                                        optionLabel="text"
                                        optionValue="value"
                                        :placeholder="$t('staff.form.select_role')"
                                        class="transparent-select"
                                        class-added="!ppv2-h-[40px] !ppv2-rounded-lg"
                                    />
                                </div>
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mb-6">
                                    <!-- <BaseSelect
                                        :label="$t('staff.prestations')"
                                        v-model="form.prestations"
                                        :options="prestationOptions"
                                        optionLabel="text"
                                        optionValue="value"
                                        :placeholder="$t('staff.form.select_prestation')"
                                        class="transparent-select"
                                        class-added="!ppv2-h-[40px] !ppv2-rounded-lg"
                                    /> -->
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- Right Column -->
                    <div class="ppv2-w-full md:ppv2-w-[35%]">
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-space-y-4">
                                <BaseSwitch
                                    :label="form.active ? $t('common.enabled') : $t('common.disabled')"
                                    v-model="form.active"
                                    :boxed="true"
                                    variant="success"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                            </div>
                        </v-card>
                    </div>
                </div>
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Bottom Actions -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" @click="cancel" />
                            <BaseButton
                                variant="primary"
                                :loading="isSaving"
                                @click="save"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                            >
                                {{ $t('common.validate') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Exit Dialog -->
        <CustomDialog v-model="showExitPopup" :defaultTitle="$t('staff.exit_dialog.title')" heightClass="ppv2-max-h-[40vh]">
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('staff.exit_dialog.description') }}
                </p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="exit">{{ $t('staff.exit_dialog.exit') }}</BaseButton>
                <BaseButton variant="primary" @click="showExitPopup = false">{{ $t('staff.exit_dialog.stay') }} </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import CustomInput from '../../Inputs/CustomInput.vue'
import BackButton from '../../Shared/BackButton.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'StaffForm',
    components: {
        CustomInput,
        BackButton,
        BaseButton,
        BaseSelect,
        BaseSwitch,
        CustomDialog,
    },
    props: {
        staffUser: { type: Object, default: () => null },
        roles: { type: Array, default: () => [] },
        prestationCategories: { type: Array, default: () => [] },
        token: { type: String, default: '' },
    },
    data() {
        return {
            isSaving: false,
            showExitPopup: false,
            required: true,
            form: {
                id: null,
                last_name: '',
                first_name: '',
                email: '',
                phone_number: '',
                password: '',
                role: '',
                prestations: [],
                active: true,
                pin: '0000',
            },
            errors: {},
            initialForm: null,
        }
    },
    computed: {
        isEdit() {
            return !!(this.staffUser && this.staffUser.id)
        },
        roleOptions() {
            // `employee` is the mandatory base "is back-office staff" marker (kept automatically by
            // the backend on save) and `client`/`super-admin` are not assignable staff roles — exclude
            // them so only the functional role is pickable.
            return this.roles
                .filter((r) => !['employee', 'client', 'super-admin'].includes(r.name))
                .map((r) => ({ text: r.name, value: r.name }))
        },
        prestationOptions() {
            return this.prestationCategories.map((p) => ({ text: p.name, value: p.id }))
        },
    },
    watch: {
        'form.phone_number'(val) {
            if (val && /\s/.test(val)) {
                this.form.phone_number = val.replace(/\s/g, '')
            }
        },
    },
    created() {
        if (this.staffUser) {
            this.initForm()
        }
        this.initialForm = JSON.stringify(this.form)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        initForm() {
            this.form = {
                id: this.staffUser.id,
                last_name: this.staffUser.last_name || '',
                first_name: this.staffUser.first_name || '',
                email: this.staffUser.email || '',
                phone_number: (this.staffUser.phone_number || '').replace(/\s/g, ''),
                password: '',
                // A staff always has `employee` (base marker) + one functional role; roles come back
                // ordered by id so roles[0] is almost always `employee`. Select the functional role
                // instead, otherwise the picker would always show/keep "employee".
                role: (this.staffUser.roles || []).map((r) => r.name).find((name) => name !== 'employee') || null,
                prestations: this.staffUser.prestations.length > 0 ? this.staffUser.prestations[0].id : null,
                active: this.staffUser.status_id === 1,
                pin: this.staffUser.pin ? this.staffUser.pin.pin : '',
            }
        },
        cancel() {
            if (JSON.stringify(this.form) !== this.initialForm) {
                this.showExitPopup = true
            } else {
                this.exit()
            }
        },
        exit() {
            window.location.href = '/back-office/v3/staffs'
        },
        validateForm() {
            this.errors = {}
            let isValid = true

            if (!this.form.last_name) {
                this.errors.last_name = this.$t('staff.form.validation.last_name_required')
                isValid = false
            }
            if (!this.form.first_name) {
                this.errors.first_name = this.$t('staff.form.validation.first_name_required')
                isValid = false
            }
            if (!this.form.email) {
                this.errors.email = this.$t('staff.form.validation.email_required')
                isValid = false
            }
            if (!this.form.phone_number) {
                this.errors.phone_number = this.$t('staff.form.validation.phone_required')
                isValid = false
            }
            if (!this.form.role) {
                this.errors.role = this.$t('staff.form.validation.role_required')
                isValid = false
            }
            if (!this.isEdit && !this.form.pin) {
                this.errors.pin = this.$t('staff.form.validation.pin_required')
                isValid = false
            }

            return isValid
        },
        async save() {
            if (!this.validateForm()) {
                this.$toast.error(this.$t('common.formValidationError'))
                return
            }

            this.isSaving = true
            try {
                const url = this.isEdit ? `/api/v3/staffs/${this.form.id}` : '/api/v3/staffs/store'
                const method = this.isEdit ? 'put' : 'post'

                const data = {
                    ...this.form,
                    status_id: this.form.active ? 1 : 2,
                    planning_visible: 1,
                    calendar_visible: 1,
                }

                await axios[method](url, data, { headers: { Authorization: `Bearer ${this.token}` } })
                this.$toast.success(this.$t('common.success'))
                this.initialForm = JSON.stringify(this.form)
                this.exit()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.data && error.response.data.errors) {
                    this.errors = error.response.data.errors
                }
            } finally {
                this.isSaving = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
.staff-v3-container {
    padding: 0;
}

.title-header {
    letter-spacing: -0.02em;
}

:deep(.v-application--wrap) {
    min-height: auto;
}

:deep(.v-card) {
    border-radius: 10px !important;
}
</style>
